import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Archive, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

const CYCLE_COLOR = { "Primaire": "bg-sky-100 text-sky-700", "Collège": "bg-emerald-100 text-emerald-700", "Lycée": "bg-violet-100 text-violet-700", "Autre": "bg-slate-100 text-slate-600" };
const STC = { Actif: "bg-emerald-100 text-emerald-700", Inactif: "bg-amber-100 text-amber-700", "Archivé": "bg-slate-100 text-slate-500" };
const CYCLES = ["Primaire", "Collège", "Lycée", "Autre"];

export default function Levels() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canWrite = hasRole("super_admin", "direction", "administration", "coordinateur");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { code: "", name: "", cycle: "Primaire", order_index: 0, status: "Actif" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get("/school/levels").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const visibleRows = showArchived ? rows : rows.filter((l) => l.status !== "Archivé");

  const openNew = () => { setForm({ ...empty, order_index: rows.length }); setEditId(null); setOpen(true); };
  const openEdit = (l) => { setForm({ code: l.code, name: l.name, cycle: l.cycle || "Autre", order_index: l.order_index, status: l.status }); setEditId(l.id); setOpen(true); };

  const submit = async () => {
    try {
      const payload = { ...form, order_index: Number(form.order_index) };
      if (editId) await api.put(`/school/levels/${editId}`, payload);
      else await api.post("/school/levels", payload);
      toast.success(t("save")); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setStatus = async (l, status) => {
    if (status === "Archivé" && !window.confirm(`${t("archive")} — ${l.name} ?`)) return;
    try { await api.patch(`/school/levels/${l.id}/status`, { status }); toast.success(t("save")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="school-levels">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("officialLevels")}</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="toggle-archived-btn" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "Masquer archivés" : "Afficher archivés"}
          </Button>
          {canWrite && <Button data-testid="level-add-btn" onClick={openNew} className="rounded-full gap-2"><Plus className="h-4 w-4" />{t("add")}</Button>}
        </div>
      </div>
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("order")}</TableHead><TableHead>{t("code")}</TableHead><TableHead>{t("name")}</TableHead>
            <TableHead>{t("cycle")}</TableHead><TableHead>Élèves</TableHead><TableHead>Groupes</TableHead><TableHead>Planning</TableHead>
            <TableHead>{t("status")}</TableHead>{canWrite && <TableHead className="text-right">Actions</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {visibleRows.map((l) => (
              <TableRow key={l.id} data-testid={`level-row-${l.code}`}>
                <TableCell className="text-slate-400 tabular-nums">{l.order_index}</TableCell>
                <TableCell className="font-medium text-slate-800">{l.code}</TableCell>
                <TableCell className="text-slate-700">{l.name}</TableCell>
                <TableCell><Badge className={`${CYCLE_COLOR[l.cycle] || "bg-slate-100"} border-0`}>{l.cycle}</Badge></TableCell>
                <TableCell className="tabular-nums text-slate-600">{l.student_count ?? 0}</TableCell>
                <TableCell className="tabular-nums text-slate-600">{l.group_count ?? 0}</TableCell>
                <TableCell className="tabular-nums text-slate-600">{l.planning_count ?? 0}</TableCell>
                <TableCell><Badge className={`${STC[l.status] || "bg-slate-100"} border-0`}>{l.status}</Badge></TableCell>
                {canWrite && <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" data-testid={`level-edit-${l.code}`} onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                    {l.status !== "Archivé" && (l.status === "Actif"
                      ? <Button size="icon" variant="ghost" title={t("deactivate") || "Désactiver"} onClick={() => setStatus(l, "Inactif")}><PowerOff className="h-4 w-4 text-amber-600" /></Button>
                      : <Button size="icon" variant="ghost" title={t("activate")} data-testid={`level-activate-${l.code}`} onClick={() => setStatus(l, "Actif")}><Power className="h-4 w-4 text-emerald-600" /></Button>)}
                    {l.status !== "Archivé" && <Button size="icon" variant="ghost" data-testid={`level-archive-${l.code}`} onClick={() => setStatus(l, "Archivé")}><Archive className="h-4 w-4 text-slate-500" /></Button>}
                  </div>
                </TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editId ? t("edit") : t("add")} — {t("officialLevels")}</DialogTitle>
            <DialogDescription className="sr-only">{t("officialLevels")}</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <Input data-testid="level-code" placeholder={t("code")} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
            <Input data-testid="level-name" placeholder={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Select value={form.cycle} onValueChange={(v) => setForm({ ...form, cycle: v })}>
              <SelectTrigger data-testid="level-cycle"><SelectValue /></SelectTrigger>
              <SelectContent>{CYCLES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="level-order" type="number" placeholder={t("order")} value={form.order_index} onChange={(e) => setForm({ ...form, order_index: e.target.value })} />
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="level-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["Actif", "Inactif"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button data-testid="level-save-btn" onClick={submit}>{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
